import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { trackCTAClick } from '../../../utils/analytics';

const HeroSection = () => {
  const handleGetStarted = () => {
    trackCTAClick('Get Started', 'hero_section');
  };

  const handleWatchDemo = () => {
    trackCTAClick('Watch Demo', 'hero_section');
  };

  return (
    <div className="relative bg-gradient-to-br from-primary via-primary to-secondary text-primary-foreground py-16 md:py-20 lg:py-24 px-4 md:px-6 lg:px-8 overflow-hidden">
      {/* Corporate Background Image */}
      <div 
        className="absolute inset-0 opacity-50"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/70 via-primary/65 to-secondary/70" />
      
      {/* Content */}
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div>
            <div className="text-xs md:text-sm opacity-80 mb-4 flex items-center gap-2">
              <Icon name="Globe" size={16} />
              <span>Serving Global Clients in UK 🇬🇧 | US 🇺🇸 | Canada 🇨🇦 | UAE 🇦🇪 | India 🇮🇳</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">
              Your Global Extended
              <br />
              <span className="text-accent">Operations Partner</span>
            </h1>
            
            <p className="text-base md:text-lg lg:text-xl opacity-90 mb-8 md:mb-10">
              Trusted by international businesses across UK, US, Canada, UAE, and India — delivering seamless operations 24×7, 365 days a year
            </p>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-3 mb-2">
                  <Icon name="Clock" size={28} className="text-white" />
                  <div className="text-4xl font-bold">60%</div>
                </div>
                <h3 className="text-lg font-semibold mb-1">Time Savings</h3>
                <p className="text-sm opacity-90">Focus on core business globally</p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-3 mb-2">
                  <Icon name="TrendingDown" size={28} className="text-white" />
                  <div className="text-4xl font-bold">25%</div>
                </div>
                <h3 className="text-lg font-semibold mb-1">Cost Reduction</h3>
                <p className="text-sm opacity-90">Optimized international operations</p>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-accent hover:bg-accent/90 text-white" onClick={handleGetStarted}>
                <Icon name="Play" size={18} className="mr-2" />
                Get Started
              </Button>
              <Button size="lg" variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white/10" onClick={handleWatchDemo}>
                <Icon name="Video" size={18} className="mr-2" />
                Watch Demo
              </Button>
            </div>
          </div>

          {/* Right Content - Complete Operations Management Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
              <h3 className="text-lg font-bold mb-2">Global Operations Management</h3>
            </div>
            
            <div className="bg-accent/80 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="flex flex-col items-center text-center">
                <Icon name="Clock" size={32} className="mb-3" />
                <h4 className="text-2xl font-bold mb-1">24×7</h4>
                <p className="text-sm opacity-90">Global Support</p>
              </div>
            </div>

            <div className="bg-success/80 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="flex flex-col items-center text-center">
                <Icon name="Globe" size={32} className="mb-3" />
                <h4 className="text-2xl font-bold mb-1">5+</h4>
                <p className="text-sm opacity-90">Countries</p>
              </div>
            </div>

            <div className="bg-purple-500/80 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="flex flex-col items-center text-center">
                <Icon name="Users" size={32} className="mb-3" />
                <h4 className="text-2xl font-bold mb-1">Expert</h4>
                <p className="text-sm opacity-90">Global Teams</p>
              </div>
            </div>

            <div className="bg-orange-500/80 backdrop-blur-sm rounded-xl p-6 border border-white/20">
              <div className="flex flex-col items-center text-center">
                <Icon name="DollarSign" size={32} className="mb-3" />
                <h4 className="text-2xl font-bold mb-1">Multi-Currency</h4>
                <p className="text-sm opacity-90">Pricing</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* EBAS Venture Private Limited Description */}
        <div className="mt-12 pt-8 border-t border-white/20">
          <div className="flex flex-col md:flex-row items-center justify-center gap-3 text-center">
            <div className="flex items-center gap-2">
              <Icon name="Building2" size={20} className="text-accent" />
              <span className="text-sm md:text-base font-medium opacity-90">
                ZippyAssist is operated by
              </span>
            </div>
            <span className="text-base md:text-lg font-bold text-accent">
              EBAS Venture Private Limited
            </span>
          </div>
          <p className="text-xs md:text-sm opacity-75 text-center mt-2 max-w-2xl mx-auto">
            A trusted corporate entity delivering world-class business solutions with global reach and local expertise
          </p>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;